// core/services/billingService.ts — real Stripe-backed billing (1.23.00).
//
// Replaces authService.setPremium (POST /users/me/premium/ — DELETED, see
// backend apps.billing). Checkout/portal hand back a Stripe-hosted URL only;
// they never write premium/tier themselves — apps.billing's webhook is the
// sole grant path. Components must not import this directly; use
// hooks/useBilling.ts (mirrors the premiumService/usePremium convention).

import { api, ApiError } from '@/core/lib/apiClient';
import type { BillingInfo } from '@/core/models/billing';

/**
 * Why this is a union and not `string | null`: POST /billing/checkout/ has
 * TWO distinct non-success answers and they call for opposite UI. `null` could
 * only ever mean one of them, so the other (409) escaped as a thrown error and
 * every caller reported "something went wrong, please try again" — to a member
 * whose only problem was that they already had what they were trying to buy.
 */
export type CheckoutStart =
  /** Stripe handed back a hosted Checkout URL — send the caller there. */
  | { kind: 'url'; url: string }
  /** 503 `billing_not_configured` — STRIPE_* is unset server-side, so no
   * payment URL exists at all. Callers render the disabled affordance. */
  | { kind: 'unconfigured' }
  /** 409 `already_subscribed` — the server refused because this account
   * already holds Pro. Nothing was charged (the server is the gate); the
   * CLIENT's tier was merely stale, which is why the upgrade control was on
   * screen at all. Callers should say so and re-read billing state. */
  | { kind: 'already_subscribed' };

export const billingService = {
  /** The caller's own tier/status. Never 503s — always at least "free". */
  async me(): Promise<BillingInfo> {
    return api<BillingInfo>('/billing/me/');
  },

  /**
   * Starts a Stripe Checkout session for Pro. Returns which of the three
   * answers the server gave (see CheckoutStart). Any OTHER failure re-throws
   * — an unrecognized failure is a real error and must not be flattened into
   * one of these named, benign outcomes.
   */
  async checkout(): Promise<CheckoutStart> {
    try {
      const { url } = await api<{ url: string }>('/billing/checkout/', { method: 'POST' });
      return { kind: 'url', url };
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === 'billing_not_configured') return { kind: 'unconfigured' };
        if (e.code === 'already_subscribed') return { kind: 'already_subscribed' };
      }
      throw e;
    }
  },

  /** Opens the Stripe billing portal (cancel/manage) and returns its hosted
   * URL, or null when unconfigured/no existing subscription. */
  async portal(): Promise<string | null> {
    try {
      const { url } = await api<{ url: string }>('/billing/portal/', { method: 'POST' });
      return url;
    } catch (e) {
      if (
        e instanceof ApiError &&
        (e.code === 'billing_not_configured' || e.code === 'no_subscription')
      ) {
        return null;
      }
      throw e;
    }
  },
} as const;
